import React, { useContext, useState } from 'react';
import {
  Box, Typography, IconButton, Avatar, Menu, MenuItem,
  Divider, Tooltip, InputBase,
} from '@mui/material';
import {
  MenuOpen, Menu as MenuIcon, Search, DarkMode, LightMode,
  Notifications, Logout, Person, Settings, KeyboardArrowDown,
} from '@mui/icons-material';
import { AuthContext } from '../context/AuthContext';
import { useThemeMode } from '../context/ThemeContext';
import { useNavigate, useLocation } from 'react-router-dom';

function Breadcrumb() {
  const location = useLocation();
  const segments = location.pathname.replace('/admin', '').split('/').filter(Boolean);

  const labels = {
    pos: 'POS Terminal', orders: 'Online Orders', reports: 'Sales Reports',
    shifts: 'Shift Management', products: 'Products', categories: 'Categories',
    inventory: 'Inventory', discounts: 'Discounts', suppliers: 'Suppliers',
    customers: 'Customers', 'flash-deals': 'Flash Deals',
    'featured-products': 'Featured Products', 'quick-picks': 'Quick Picks',
    deals: 'Deals of the Day', content: 'Hero Banners', campaigns: 'Campaigns',
    segments: 'Segments', 'notification-center': 'Notifications',
    analytics: 'Analytics', users: 'Users', riders: 'Riders',
    'rider-orders': 'Rider Orders', 'app-config': 'App Config',
    'audit-log': 'Audit Log', system: 'System Health',
    'live-tracking': 'Live Tracking', 'customer-tracking': 'Customer Map',
    'image-recovery': 'Image Recovery', profile: 'Profile',
  };

  if (!segments.length) return (
    <Typography sx={{ fontSize: 14, fontWeight: 600, color: 'text.primary' }}>Dashboard</Typography>
  );

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <Typography sx={{ fontSize: 13, color: 'text.secondary', fontWeight: 500 }}>Admin</Typography>
      <Typography sx={{ fontSize: 13, color: 'text.disabled', mx: 0.3 }}>/</Typography>
      <Typography sx={{ fontSize: 13.5, fontWeight: 600, color: 'text.primary' }}>
        {labels[segments[segments.length - 1]] || segments[segments.length - 1]}
      </Typography>
    </Box>
  );
}

export default function AdminHeader({ collapsed, onMenuToggle }) {
  const { user, logout } = useContext(AuthContext);
  const { mode, toggleMode } = useThemeMode();
  const navigate = useNavigate();
  const dark = mode === 'dark';

  const [anchorEl, setAnchorEl] = useState(null);
  const [searchFocused, setSearchFocused] = useState(false);

  const handleMenuOpen  = (e) => setAnchorEl(e.currentTarget);
  const handleMenuClose = ()  => setAnchorEl(null);
  const handleLogout    = ()  => { handleMenuClose(); logout(); };
  const handleProfile   = ()  => { handleMenuClose(); navigate('/admin/profile'); };
  const handleSettings  = ()  => { handleMenuClose(); navigate('/admin/app-config'); };

  const initials = (user?.name || 'A').split(' ').map((s) => s[0]).join('').toUpperCase().slice(0, 2);

  return (
    <Box
      sx={{
        height: 56,
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        px: 2,
        gap: 1,
        bgcolor: dark ? '#111827' : '#ffffff',
        borderBottom: `1px solid ${dark ? 'rgba(148,163,184,0.08)' : 'rgba(0,0,0,0.07)'}`,
        position: 'sticky',
        top: 0,
        zIndex: 50,
      }}
    >
      {/* Collapse toggle */}
      <Tooltip title={collapsed ? 'Expand' : 'Collapse'} placement="bottom">
        <IconButton
          onClick={onMenuToggle}
          size="small"
          sx={{
            color: 'text.secondary',
            '&:hover': { color: 'text.primary', bgcolor: dark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)' },
          }}
        >
          {collapsed ? <MenuIcon sx={{ fontSize: 20 }} /> : <MenuOpen sx={{ fontSize: 20 }} />}
        </IconButton>
      </Tooltip>

      {/* Breadcrumb */}
      <Box sx={{ flex: 1, ml: 0.5 }}>
        <Breadcrumb />
      </Box>

      {/* Search */}
      <Box sx={{
        display: 'flex', alignItems: 'center', gap: 1,
        px: 1.5, py: 0.6,
        borderRadius: 2,
        border: `1px solid ${searchFocused
          ? '#6366f1'
          : dark ? 'rgba(148,163,184,0.15)' : 'rgba(0,0,0,0.12)'}`,
        bgcolor: dark ? 'rgba(255,255,255,0.03)' : '#f9fafb',
        width: searchFocused ? 220 : 160,
        transition: 'all 0.2s ease',
        cursor: 'text',
      }}>
        <Search sx={{ fontSize: 15, color: 'text.secondary', flexShrink: 0 }} />
        <InputBase
          placeholder="Search…"
          onFocus={() => setSearchFocused(true)}
          onBlur={() => setSearchFocused(false)}
          sx={{
            fontSize: 13, color: 'text.primary',
            '& input::placeholder': { color: 'text.secondary', opacity: 1, fontSize: 13 },
            flex: 1, minWidth: 0,
          }}
        />
      </Box>

      {/* Dark mode toggle */}
      <Tooltip title={dark ? 'Light mode' : 'Dark mode'} placement="bottom">
        <IconButton
          onClick={toggleMode}
          size="small"
          sx={{
            color: 'text.secondary',
            '&:hover': { color: 'text.primary', bgcolor: dark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)' },
          }}
        >
          {dark ? <LightMode sx={{ fontSize: 18 }} /> : <DarkMode sx={{ fontSize: 18 }} />}
        </IconButton>
      </Tooltip>

      {/* Notifications */}
      <Tooltip title="Notifications" placement="bottom">
        <IconButton
          size="small"
          sx={{
            color: 'text.secondary',
            '&:hover': { color: 'text.primary', bgcolor: dark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)' },
          }}
        >
          <Notifications sx={{ fontSize: 18 }} />
        </IconButton>
      </Tooltip>

      {/* User menu */}
      <Box
        onClick={handleMenuOpen}
        sx={{
          display: 'flex', alignItems: 'center', gap: 1,
          pl: 1, pr: 0.5, py: 0.5,
          borderRadius: 2,
          cursor: 'pointer',
          border: `1px solid transparent`,
          transition: 'all 0.15s ease',
          '&:hover': {
            bgcolor: dark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)',
            borderColor: dark ? 'rgba(148,163,184,0.15)' : 'rgba(0,0,0,0.10)',
          },
        }}
      >
        <Avatar
          src={user?.image}
          sx={{
            width: 28, height: 28,
            fontSize: 11, fontWeight: 700,
            background: 'linear-gradient(135deg,#6366f1 0%,#8b5cf6 100%)',
          }}
        >
          {!user?.image && initials}
        </Avatar>
        <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
          <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: 'text.primary', lineHeight: 1.2 }}>
            {user?.name?.split(' ')[0] || 'Admin'}
          </Typography>
          <Typography sx={{ fontSize: 10.5, color: 'text.secondary', lineHeight: 1 }}>
            {user?.role || 'admin'}
          </Typography>
        </Box>
        <KeyboardArrowDown sx={{ fontSize: 15, color: 'text.secondary' }} />
      </Box>

      {/* Dropdown */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        {/* User info header */}
        <Box sx={{ px: 2, py: 1.5, minWidth: 200 }}>
          <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: 'text.primary' }}>
            {user?.name || 'Admin'}
          </Typography>
          <Typography sx={{ fontSize: 12, color: 'text.secondary', mt: 0.2 }}>
            {user?.email}
          </Typography>
        </Box>
        <Divider />
        <MenuItem onClick={handleProfile} sx={{ gap: 1.2, mt: 0.5 }}>
          <Person sx={{ fontSize: 16, color: 'text.secondary' }} />
          <Typography fontSize={13.5}>My Profile</Typography>
        </MenuItem>
        <MenuItem onClick={handleSettings} sx={{ gap: 1.2 }}>
          <Settings sx={{ fontSize: 16, color: 'text.secondary' }} />
          <Typography fontSize={13.5}>App Settings</Typography>
        </MenuItem>
        <MenuItem onClick={toggleMode} sx={{ gap: 1.2 }}>
          {dark
            ? <LightMode sx={{ fontSize: 16, color: 'text.secondary' }} />
            : <DarkMode   sx={{ fontSize: 16, color: 'text.secondary' }} />
          }
          <Typography fontSize={13.5}>{dark ? 'Light Mode' : 'Dark Mode'}</Typography>
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleLogout} sx={{ gap: 1.2, mb: 0.5, color: 'error.main' }}>
          <Logout sx={{ fontSize: 16, color: 'error.main' }} />
          <Typography fontSize={13.5} color="error.main">Sign Out</Typography>
        </MenuItem>
      </Menu>
    </Box>
  );
}
